import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useNavigation } from "../hooks/useNavigation";
import jatsaPeticionService from "../services/jatsaPeticionService";
import ssomaService from "../services/ssomaService";
import { pathWithEmpresa } from "../utils/companyUrl";
import {
  mensajePeticionJatsa,
  peticionJatsaExitosa,
  tituloPeticionJatsa,
} from "../utils/jatsaIngestaVisible";

function labelUmbral(umbral) {
  if (umbral === "1h") return "1 hora";
  if (umbral === "2d") return "2 días";
  if (umbral === "3d") return "3 días";
  if (umbral === "7d") return "1 semana";
  if (umbral === "vencido") return "Vencido";
  if (umbral === "sctr") return "Falta SCTR";
  if (umbral === "emo") return "Falta EMO";
  if (umbral === "epp") return "Falta EPP";
  if (umbral === "curso") return "Falta curso";
  if (umbral === "abierto") return "Abierto";
  return "";
}

function irASsoma(navigate, location, empresaId, item) {
  const params = new URLSearchParams();
  if (item?.empleado_id) params.set("empleado", String(item.empleado_id));
  if (item?.referencia_tipo === "accidente" && item?.referencia_id) {
    params.set("accidente", String(item.referencia_id));
  }
  const extra = params.toString();
  const destino = pathWithEmpresa(extra ? `/ssoma?${extra}` : "/ssoma", empresaId);
  const enSsoma = location.pathname === "/ssoma";

  window.dispatchEvent(
    new CustomEvent("ssoma-abrir-notificacion", {
      detail: {
        empleadoId: item?.empleado_id || null,
        accidenteId: item?.referencia_tipo === "accidente" ? item?.referencia_id : null,
      },
    }),
  );

  if (!enSsoma) {
    navigate(destino);
  }
}

function horaCorta(valor) {
  if (!valor) return "";
  const fecha = new Date(valor);
  if (Number.isNaN(fecha.getTime())) return "";
  return fecha.toLocaleString("es-PE");
}

const CampanaNotificaciones = () => {
  const { selectedCompany } = useAuth();
  const { ssomaActivado } = useNavigation();
  const navigate = useNavigate();
  const location = useLocation();
  const empresaId = selectedCompany?.id;
  const panelRef = useRef(null);

  const [abierto, setAbierto] = useState(false);
  const [items, setItems] = useState([]);
  const [sinLeer, setSinLeer] = useState(0);
  const [jatsaActivo, setJatsaActivo] = useState(false);

  const cargar = useCallback(async () => {
    if (!empresaId) {
      setItems([]);
      setSinLeer(0);
      setJatsaActivo(false);
      return;
    }

    let ssomaItems = [];
    let ssomaSinLeer = 0;
    if (ssomaActivado) {
      try {
        const resp = await ssomaService.notificaciones(empresaId);
        if (resp.success) {
          ssomaItems = (resp.data || []).map((item) => ({
            ...item,
            origen: "ssoma",
            clave: `ssoma-${item.id}`,
          }));
          ssomaSinLeer = resp.sin_leer || 0;
        }
      } catch (error) {
        console.error("No se pudieron cargar las notificaciones SSOMA", error);
      }
    }

    let jatsaItems = [];
    let jatsaSinLeer = 0;
    let enabled = false;
    try {
      const resp = await jatsaPeticionService.listar(empresaId);
      enabled = Boolean(resp?.jatsa_ingesta_enabled);
      if (resp?.success) {
        jatsaItems = (resp.data || []).map((item) => ({
          ...item,
          origen: "jatsa",
          clave: `jatsa-${item.id}`,
          titulo: tituloPeticionJatsa(item),
          mensaje: mensajePeticionJatsa(item),
        }));
        jatsaSinLeer = resp.sin_leer || 0;
      }
    } catch (error) {
      console.error("No se pudieron cargar las peticiones Jatsa", error);
    }
    setJatsaActivo(enabled);

    const mezclados = [...jatsaItems, ...ssomaItems].sort((a, b) => {
      const ta = new Date(a.created_at || 0).getTime();
      const tb = new Date(b.created_at || 0).getTime();
      return tb - ta;
    });
    setItems(mezclados);
    setSinLeer(ssomaSinLeer + jatsaSinLeer);
  }, [empresaId, ssomaActivado]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  useEffect(() => {
    if (!empresaId) return undefined;
    const timer = setInterval(cargar, jatsaActivo ? 15000 : 120000);
    return () => clearInterval(timer);
  }, [empresaId, jatsaActivo, cargar]);

  useEffect(() => {
    const onClick = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        setAbierto(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  if (!empresaId) {
    return null;
  }

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        className="relative inline-flex items-center justify-center h-10 w-10 rounded-full text-gray-600 hover:bg-gray-100 hover:text-gray-900"
        title="Notificaciones"
        aria-label="Notificaciones"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.4-1.4A2 2 0 0118 14.2V11a6 6 0 10-12 0v3.2c0 .5-.2 1-.6 1.4L4 17h5m6 0a3 3 0 11-6 0"
          />
        </svg>
        {sinLeer > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[1.15rem] h-5 px-1 rounded-full bg-rose-600 text-white text-[10px] font-bold flex items-center justify-center">
            {sinLeer > 99 ? "99+" : sinLeer}
          </span>
        )}
      </button>

      {abierto && (
        <div className="absolute right-0 mt-2 w-96 max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-lg border border-gray-200 z-50 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-2">
            <div>
              <p className="text-sm font-semibold text-gray-900">Notificaciones</p>
              <p className="text-[11px] text-gray-500">
                {jatsaActivo ? "Peticiones a Jatsa" : "SSOMA y vencimientos"}
                {jatsaActivo && ssomaActivado ? " · SSOMA" : ""}
              </p>
            </div>
            {sinLeer > 0 && (
              <button
                type="button"
                onClick={async () => {
                  if (ssomaActivado) {
                    await ssomaService.marcarNotificacionesLeidas(empresaId);
                  }
                  if (jatsaActivo) {
                    await jatsaPeticionService.marcarTodasLeidas(empresaId);
                  }
                  cargar();
                }}
                className="text-xs text-teal-700 hover:underline"
              >
                Marcar leídas
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 && (
              <p className="px-4 py-6 text-sm text-gray-500 text-center">
                No hay peticiones ni recordatorios.
              </p>
            )}
            {items.map((item) => (
              <button
                key={item.clave}
                type="button"
                onClick={async () => {
                  if (item.origen === "jatsa") {
                    if (item.id && !item.leida_at) {
                      await jatsaPeticionService.marcarLeida(item.id);
                      cargar();
                    }
                    setAbierto(false);
                    return;
                  }
                  if (item.id && !item.leida_at) {
                    await ssomaService.marcarNotificacionLeida(empresaId, item.id);
                    cargar();
                  }
                  setAbierto(false);
                  irASsoma(navigate, location, empresaId, item);
                }}
                className={`w-full text-left px-4 py-3 border-b border-gray-50 hover:bg-gray-50 ${
                  item.leida_at ? "opacity-70" : "bg-teal-50/40"
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-semibold text-gray-900">{item.titulo}</p>
                  {item.origen === "jatsa" ? (
                    <span
                      className={`shrink-0 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${
                        peticionJatsaExitosa(item)
                          ? "text-teal-800 bg-teal-50"
                          : "text-rose-700 bg-rose-50"
                      }`}
                    >
                      {peticionJatsaExitosa(item) ? "OK" : "Error"}
                    </span>
                  ) : (
                    item.umbral && (
                      <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-rose-700 bg-rose-50 px-1.5 py-0.5 rounded">
                        {labelUmbral(item.umbral)}
                      </span>
                    )
                  )}
                </div>
                <p className="text-xs text-gray-600 mt-0.5">{item.mensaje}</p>
                {item.created_at && (
                  <p className="text-[10px] text-gray-400 mt-1">{horaCorta(item.created_at)}</p>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CampanaNotificaciones;
